import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { TimezoneSelect } from 'components/AsyncFields';
import { OrganizationsActions } from 'state/modules/organizations';
import { countries, states as stateslist } from 'constants';

import { TextField, PhoneField, MaterialSelectField } from 'components/FormFields';
import { LocalesSelect } from 'components/DataFields';
import { zip_by_country } from 'utils';

const UpdateOrgSettingsInfoModal = ({ id, open, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [states, setStates] = useState([]);

  const { organization, fetchedUpdateOrgSuccess, loading, errors } = useSelector(
    (state) => state.organizations
  );

  // Validation schema of update user form
  const UpdateOrganizationInfoSchema = Yup.object().shape({
    email: Yup.string(),
    zip_code: zip_by_country('US', 'CA', 'MX', 'AU', 'NZ'),
    time_zone: Yup.string(),
    description: Yup.string().max(
      200,
      i18n.t('common.max_long', {
        max_long: 200,
      })
    ),
    phone_number: Yup.string(),
    name: Yup.string().required(i18n.t('common.required_field')),
    state: Yup.string(),
    country: Yup.string(),
    address: Yup.string(),
    locale: Yup.string(),
  });

  // Initials values of the form
  const initialValues = {
    name: organization?.name || '',
    email: organization?.email || '',
    description: organization?.description || '',
    phone_number: organization?.phone_number || '',
    address: organization?.address || '',
    state: organization?.state || '',
    zip_code: organization?.zip_code || '',
    time_zone: organization?.time_zone || '',
    country: organization?.country || '',
    locale: organization?.locale || '',
  };

  useEffect(() => {
    if (id) {
      dispatch(OrganizationsActions.fetchOrganizationById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (organization && organization?.country) {
      setStates(stateslist[organization?.country?.toLowerCase()] || []);
    }
  }, [organization]);

  useEffect(() => {
    if (fetchedUpdateOrgSuccess) {
      handleClick();
      dispatch(OrganizationsActions.resetUpdateOrganization());
    }
  }, [fetchedUpdateOrgSuccess, handleClick, dispatch]);

  /**
   * Handle form submit
   * @param {object} values Object with the value of fields of the form
   */
  const onSubmit = (values, { setErrors }) => {
    if (id) {
      const data = { ...values };
      const fields = ['description', 'address'];

      // remove empty fields
      Object.keys(data).forEach(
        (key) => !data[key] && data[key] !== false && !fields.includes(key) && delete data[key]
      );

      if (!data.zip_code) {
        data.zip_code = null;
      }

      if (data?.phone_number === `+${data?.phone_code}`) {
        return setErrors({ phone_number: i18n.t('common.required_field') });
      }
      delete data.phone_code;

      dispatch(OrganizationsActions.fetchUpdateOrganization(id, data));
    }
  };

  const changeState = (e) => {
    if (e?.target?.name === 'country') {
      const country = e?.target?.value?.toLowerCase();
      setStates(stateslist[country || 'us']);
    }
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      loading={loading}
      onEnter={handleSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      enterButtonLabel={i18n.t('common.update')}
      title={i18n.t('organization.update_organization_info')}
    >
      <Formik
        innerRef={formRef}
        onSubmit={onSubmit}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={UpdateOrganizationInfoSchema}
      >
        {({ handleSubmit, values }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h3">
                  <Box fontSize={13} mt={2} fontWeight="bold">
                    {i18n.t('organization.basic_info')}
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Field
                    name="name"
                    margin="none"
                    component={TextField}
                    label={i18n.t('organization.name')}
                    asyncErrors={errors?.name}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Field
                    name="email"
                    margin="none"
                    required={false}
                    component={TextField}
                    label={i18n.t('organization.email')}
                    asyncErrors={errors?.email}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Field
                    margin="none"
                    required={false}
                    name="phone_number"
                    dialcodename="phone_code"
                    asyncErrors={errors?.phone_number}
                    label={i18n.t('organization.phone')}
                    component={organization?.phone_number ? PhoneField : TextField}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Field
                    name="description"
                    margin="none"
                    required={false}
                    component={TextField}
                    label={i18n.t('organization.description')}
                    asyncErrors={errors?.description}
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h3">
                  <Box fontSize={13} mt={4} fontWeight="bold">
                    {i18n.t('account.contact_info')}
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Field
                    name="country"
                    options={countries}
                    handleChange={changeState}
                    component={MaterialSelectField}
                    placeholder={i18n.t('location.country')}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Field
                    name="address"
                    margin="none"
                    required={false}
                    component={TextField}
                    label={i18n.t('organization.address')}
                    asyncErrors={errors?.address}
                  />
                </Box>
              </Grid>
              {states && (
                <Grid item xs={6}>
                  <Box>
                    <Field
                      name="state"
                      options={states || []}
                      readOnly={states?.length === 0 || !states}
                      component={MaterialSelectField}
                      asyncErrors={errors?.state}
                      placeholder={i18n.t('organization.state')}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={6}>
                <Box>
                  <Field
                    name="zip_code"
                    margin="none"
                    required={false}
                    component={TextField}
                    label={i18n.t('organization.zip_code')}
                    asyncErrors={errors?.zip_code}
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h3">
                  <Box fontSize={13} mt={4} fontWeight="bold">
                    {i18n.t('organization.time_zone')}
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Field
                    name="time_zone"
                    margin="none"
                    country={values.country}
                    component={TimezoneSelect}
                    label={i18n.t('organization.time_zone')}
                    asyncErrors={errors?.timezone}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Field
                    name="locale"
                    margin="none"
                    component={LocalesSelect}
                    asyncErrors={errors?.locale}
                  />
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

UpdateOrgSettingsInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

UpdateOrgSettingsInfoModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default UpdateOrgSettingsInfoModal;
